import { computed, Signal } from '@angular/core';
import {
  ClientInvoiceDraftItem,
  ClientOrderItem,
  DocumentSections,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceSection,
  roundAmount,
} from 'shared';

export type SupplierInvoiceSelectors = ReturnType<typeof createSupplierInvoiceSelectors>;

export const createSupplierInvoiceSelectors = (ctx: {
  sections: Signal<SupplierInvoiceSection[] | null>;
  supplierInvoiceItemsSections: Signal<DocumentSections<SupplierInvoiceItem> | null>;
  supplierInvoiceItemMappingsSections: Signal<DocumentSections<SupplierInvoiceItemMapping> | null>;
  clientOrderItemsSections: Signal<DocumentSections<ClientOrderItem> | null>;
  clientInvoiceDraftItemsSections: Signal<DocumentSections<ClientInvoiceDraftItem> | null>;
}) => {
  const itemSectionByIri = computed(() => {
    const sections = ctx.supplierInvoiceItemsSections() ?? {};
    const map = new Map<string, string>();
    for (const [sectionIri, items] of Object.entries(sections)) {
      for (const it of (items as SupplierInvoiceItem[]) ?? []) {
        const iri = it['@id'];
        if (iri) map.set(iri, sectionIri);
      }
    }
    return map;
  });

  const allSupplierInvoiceItems: Signal<SupplierInvoiceItem[]> = computed(() =>
    Object.values(ctx.supplierInvoiceItemsSections() ?? {}).flat(),
  );

  const allSupplierInvoiceMappings: Signal<SupplierInvoiceItemMapping[]> = computed(() =>
    Object.values(ctx.supplierInvoiceItemMappingsSections() ?? {}).flat(),
  );

  const clientOrderItemsBySectionByIri = computed(() => {
    const sections = ctx.clientOrderItemsSections() ?? {};
    const out = new Map<string, Map<string, ClientOrderItem>>();
    for (const [sectionIri, items] of Object.entries(sections)) {
      const byIri = new Map<string, ClientOrderItem>();
      for (const it of (items as ClientOrderItem[]) ?? []) {
        const iri = it['@id'];
        if (iri) byIri.set(iri, it);
      }
      out.set(sectionIri, byIri);
    }
    return out;
  });

  const fullyMappedBySection: Signal<Map<string, boolean>> = computed(() => {
    const itemsBySection: DocumentSections<SupplierInvoiceItem> = ctx.supplierInvoiceItemsSections() ?? {};
    const mappingsBySection: DocumentSections<SupplierInvoiceItemMapping> =
      ctx.supplierInvoiceItemMappingsSections() ?? {};
    const out = new Map<string, boolean>();

    for (const [sectionIri, its] of Object.entries(itemsBySection)) {
      const items = its as SupplierInvoiceItem[];
      const mappings: SupplierInvoiceItemMapping[] = mappingsBySection[sectionIri] ?? [];
      if (!items || items.length === 0) {
        out.set(sectionIri, false);
        continue;
      }
      const fully = items.every((item) => {
        if (item.doNotInvoice) return true;
        const required = roundAmount(Number(item.amount ?? 0) || 0);
        const mapped = roundAmount(
          mappings
            .filter((m) => m.supplierInvoiceItem === item['@id'])
            .reduce((s, m) => s + (Number(m.amount) || 0), 0),
        );
        return mapped === required;
      });
      out.set(sectionIri, fully);
    }
    return out;
  });

  const hasSections = computed(() => fullyMappedBySection().size > 0);

  const fullyMappedSectionCount = computed(() => {
    let count = 0;
    for (const v of fullyMappedBySection().values()) if (v) count++;
    return count;
  });

  const fullyMappedUnprocessedSectionIris = computed<string[]>(() => {
    const fully = fullyMappedBySection();
    const sections = ctx.sections() ?? [];
    const out: string[] = [];
    for (const s of sections) {
      const iri = s['@id']!;
      if ((fully.get(iri) ?? false) && !s.fullyProcessed) out.push(iri);
    }
    return out;
  });

  const fullyMappedUnprocessedSectionMappings = computed(() => {
    const iris = fullyMappedUnprocessedSectionIris();
    const bySection = ctx.supplierInvoiceItemMappingsSections() ?? {};
    const out: SupplierInvoiceItemMapping[] = [];
    for (const iri of iris) out.push(...(bySection[iri] ?? []));
    return out;
  });

  const fullyMappedUnprocessedSectionMappingsBySection = computed(() => {
    const iris = fullyMappedUnprocessedSectionIris();
    const bySection = ctx.supplierInvoiceItemMappingsSections() ?? {};
    const out = new Map<string, SupplierInvoiceItemMapping[]>();
    for (const iri of iris) out.set(iri, bySection[iri] ?? []);
    return out;
  });

  const fullyMappedUnprocessedMappingsByItem = computed(() => {
    const all = fullyMappedUnprocessedSectionMappings();
    const byItem = new Map<string, SupplierInvoiceItemMapping[]>();
    for (const m of all) {
      const key = m.supplierInvoiceItem;
      const arr = byItem.get(key) ?? [];
      arr.push(m);
      byItem.set(key, arr);
    }
    return byItem;
  });

  const hasActivityBySection = computed(() => {
    const itemsBySection = ctx.supplierInvoiceItemsSections() ?? {};
    const mappingsBySection = ctx.supplierInvoiceItemMappingsSections() ?? {};
    const out = new Map<string, boolean>();

    for (const [iri, its] of Object.entries(itemsBySection)) {
      const items = its as SupplierInvoiceItem[];
      const hasMappings = (mappingsBySection[iri]?.length ?? 0) > 0;
      const hasDni = (items ?? []).some((it) => it.doNotInvoice);
      out.set(iri, hasMappings || hasDni);
    }
    return out;
  });

  const droppableItemIris = computed(() => {
    const items = allSupplierInvoiceItems() ?? [];
    const mappings = allSupplierInvoiceMappings() ?? [];

    const iris = new Set<string>();
    for (const item of items) {
      if (item.doNotInvoice) continue;
      const mapped = roundAmount(
        mappings.filter((m) => m.supplierInvoiceItem === item['@id']).reduce((s, m) => s + (Number(m.amount) || 0), 0),
      );
      if (mapped < roundAmount(Number(item.amount) || 0)) {
        iris.add(item['@id']!);
      }
    }
    return iris;
  });

  const invoiceLineIds = computed(() => (allSupplierInvoiceItems() ?? []).map((i) => `invoiceLine-${i.id}`));

  return {
    // indices
    itemSectionByIri,
    clientOrderItemsBySectionByIri,
    allSupplierInvoiceItems,
    allSupplierInvoiceMappings,

    // mapping/completeness
    fullyMappedBySection,
    hasSections,
    fullyMappedSectionCount,
    fullyMappedUnprocessedSectionIris,
    fullyMappedUnprocessedSectionMappings,
    fullyMappedUnprocessedSectionMappingsBySection,
    fullyMappedUnprocessedMappingsByItem,

    // activity / UI helpers
    hasActivityBySection,
    droppableItemIris,
    invoiceLineIds,
  };
};
